import { Database, Hash, Variable, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useAppStore } from '@/stores/app-store';
import { MMM_ALLOWING_SAAS } from '@/types/field-mapping-flow';
import { ModulesPanel } from './modules-panel';
import { EventFieldsPanel } from './event-fields-panel';
import { VariablesPanel } from './variables-panel';
import type { SidebarSection } from '@/types/field-mapping-flow';

const MIN_WIDTH = 300;
const MAX_WIDTH = 390;
const COLLAPSED_WIDTH = 40;

export function FlowSidebar() {
  const activeSidebarSection = useFlowBuilderStore((s) => s.activeSidebarSection);
  const setActiveSidebarSection = useFlowBuilderStore((s) => s.setActiveSidebarSection);
  const fieldMappingPanelOpen = useFlowBuilderStore((s) => s.fieldMappingPanelOpen);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const isMMMSupported = !!appCodeSAAS && (MMM_ALLOWING_SAAS as readonly string[]).includes(appCodeSAAS);
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(MIN_WIDTH);
  const resizing = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (collapsed) return;
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      const delta = startX - ev.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth, collapsed]);

  const expand = useCallback((section?: SidebarSection) => {
    if (section) setActiveSidebarSection(section);
    setCollapsed(false);
  }, [setActiveSidebarSection]);

  return (
    <div
      className="relative flex h-full shrink-0 flex-col border-l border-border bg-background transition-[width] duration-200 ease-in-out overflow-hidden"
      style={{ width: collapsed ? COLLAPSED_WIDTH : sidebarWidth }}
    >
      {/* Resize handle — only when expanded */}
      {!collapsed && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 hover:bg-primary/20 active:bg-primary/30 transition-colors"
          onMouseDown={handleResizeStart}
        />
      )}

      {/* ── Collapsed view ── */}
      <div
        className={`absolute inset-0 flex flex-col items-center pt-2 transition-opacity duration-200 ${
          collapsed ? 'opacity-100 delay-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => expand()}
          className="mb-3"
        >
          <PanelRightOpen className="h-4 w-4" />
        </Button>

        {/* Vertical icon tabs when collapsed */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => expand('modules')}
            className={`rounded-md p-1.5 transition-colors ${
              activeSidebarSection === 'modules'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted'
            }`}
            title="Modules"
          >
            <Database className="h-4 w-4" />
          </button>
          <button
            onClick={() => expand('fields')}
            className={`rounded-md p-1.5 transition-colors ${
              activeSidebarSection === 'fields'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted'
            }`}
            title="Event Fields"
          >
            <Hash className="h-4 w-4" />
          </button>
          {isMMMSupported && (
            <button
              onClick={() => expand('variables')}
              className={`rounded-md p-1.5 transition-colors ${
                activeSidebarSection === 'variables'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              title="Variables"
            >
              <Variable className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Expanded view ── */}
      <div
        className={`flex h-full flex-col transition-opacity duration-200 ${
          collapsed ? 'opacity-0 pointer-events-none' : 'opacity-100 delay-75'
        }`}
        style={{ minWidth: MIN_WIDTH }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <h3 className="text-xs font-semibold text-foreground">Control Library</h3>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setCollapsed(true)}
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeSidebarSection}
          onValueChange={(val) => setActiveSidebarSection(val as SidebarSection)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <TabsList className="mx-3 mt-2 w-auto">
            <TabsTrigger value="modules" className="gap-1 text-[11px]" disabled={fieldMappingPanelOpen}>
              <Database className="h-3 w-3" />
              Modules
            </TabsTrigger>
            <TabsTrigger value="fields" className="gap-1 text-[11px]">
              <Hash className="h-3 w-3" />
              Fields
            </TabsTrigger>
            {isMMMSupported && (
              <TabsTrigger value="variables" className="gap-1 text-[11px]">
                <Variable className="h-3 w-3" />
                Variables
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="modules" className="flex-1 overflow-hidden">
            <ModulesPanel />
          </TabsContent>
          <TabsContent value="fields" className="flex-1 overflow-hidden">
            <EventFieldsPanel />
          </TabsContent>
          {isMMMSupported && (
            <TabsContent value="variables" className="flex-1 overflow-hidden">
              <VariablesPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
